import { useState } from "react";
import { usePagedDocuments } from "@/hooks/usePagedDocuments";
import { Link, useNavigate } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { TemplatePicker } from "@/components/email-builder/TemplatePicker";
import { Button } from "@/components/ui/button";

export default function EmailDocumentsPage() {
  const navigate = useNavigate();
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { documents, totalPages, isLoading, error } = usePagedDocuments(currentPage, 12, "Failed to load documents");

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Published":
        return "bg-green-500/10 text-green-500";
      case "Draft":
        return "bg-yellow-500/10 text-yellow-500";
      case "Archived":
        return "bg-muted text-muted-foreground";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: diffDays > 365 ? "numeric" : undefined,
    });
  };

  return (
    <ProtectedRoute>
      <div className="lw-email-documents-page container mx-auto px-4 py-8">
        <div className="lw-email-documents-content max-w-7xl mx-auto">
          {/* Header */}
          <div className="lw-email-documents-header flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Email Templates</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Create and manage your email templates
              </p>
            </div>
            <Button
              onClick={() => setShowTemplatePicker(true)}
              className="lw-email-documents-create-btn flex items-center gap-2"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Email
            </Button>
          </div>

          {/* Error State */}
          {error && (
            <div className="lw-email-documents-error rounded-lg border border-destructive/50 bg-destructive/10 p-4 mb-6">
              <div className="flex items-center gap-2">
                <svg
                  className="h-5 w-5 text-destructive"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-sm text-destructive">{error}</p>
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="lw-email-documents-loading flex items-center justify-center py-16">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary" />
                Loading…
              </div>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && documents.length === 0 && (
            <div className="lw-email-documents-empty rounded-lg border border-dashed border-border p-16 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="mt-4 text-base font-semibold text-foreground">
                No emails yet
              </h3>
              <p className="mt-1.5 text-sm text-muted-foreground max-w-xs mx-auto">
                Create your first email — start from a template or a blank canvas.
              </p>
              <button
                onClick={() => setShowTemplatePicker(true)}
                className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Email
              </button>
            </div>
          )}

          {/* Documents Grid */}
          {!isLoading && !error && documents.length > 0 && (
            <>
              <div className="lw-email-documents-grid grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {documents.map((document) => (
                  <Link
                    key={document.id}
                    to={`/campaigns/email/${document.id}`}
                    className="lw-email-document-card group flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm transition-all duration-200 hover:shadow-md hover:border-primary/40 hover:-translate-y-0.5"
                  >
                    {/* Preview/Thumbnail — email layout sketch */}
                    <div className="relative h-40 overflow-hidden bg-background p-4">
                      <div className="flex flex-col gap-2 h-full">
                        {/* Header bar */}
                        <div className="h-3 rounded-sm bg-primary/20 w-full" />
                        {/* Spacer */}
                        <div className="h-2 rounded-sm bg-muted-foreground/10 w-3/4" />
                        {/* Content lines */}
                        <div className="h-1.5 rounded-sm bg-muted-foreground/15 w-full" />
                        <div className="h-1.5 rounded-sm bg-muted-foreground/15 w-5/6" />
                        <div className="h-1.5 rounded-sm bg-muted-foreground/10 w-4/5" />
                        {/* CTA button shape */}
                        <div className="h-4 rounded-sm bg-primary/20 w-1/3 mx-auto mt-1" />
                        {/* Footer lines */}
                        <div className="h-1.5 rounded-sm bg-muted-foreground/10 w-full mt-1" />
                        <div className="h-1.5 rounded-sm bg-muted-foreground/10 w-2/3" />
                      </div>

                      {/* Status Badge */}
                      <div className="absolute left-3 top-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${getStatusColor(
                            document.status
                          )}`}
                        >
                          {document.status}
                        </span>
                      </div>
                    </div>

                    {/* Document Info */}
                    <div className="flex flex-1 flex-col p-4">
                      <h3 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-1">
                        {document.name}
                      </h3>

                      <div className="mt-2 flex-1 text-xs text-muted-foreground space-y-1">
                        <div className="flex items-center gap-1">
                          <svg
                            className="h-3.5 w-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span>Updated {formatDate(document.updatedAt)}</span>
                        </div>
                        {document.campaigns && document.campaigns.length > 0 && (
                          <div className="flex items-center gap-1">
                            <svg
                              className="h-3.5 w-3.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76"
                              />
                            </svg>
                            <span>{document.campaigns.length} campaign{document.campaigns.length !== 1 ? 's' : ''}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <svg
                            className="h-3.5 w-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                            />
                          </svg>
                          <span>Version {document.currentVersion}</span>
                        </div>
                      </div>

                      <div className="mt-4 flex items-center justify-between pt-3 border-t border-border">
                        <span className="text-xs text-muted-foreground">
                          {(document.blocks || []).length} block{(document.blocks || []).length !== 1 ? 's' : ''}
                        </span>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate(`/campaigns/new?documentId=${document.id}`);
                            }}
                            className="text-xs text-muted-foreground hover:text-primary transition-colors"
                          >
                            + Campaign
                          </button>
                          <div className="text-xs font-medium text-primary group-hover:underline">
                            Open →
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="lw-email-documents-pagination mt-8 flex items-center justify-center gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="rounded-md border border-border bg-background px-3 py-2 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <span className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="rounded-md border border-border bg-background px-3 py-2 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Template Picker Modal */}
      {showTemplatePicker && (
        <TemplatePicker onClose={() => setShowTemplatePicker(false)} />
      )}
    </ProtectedRoute>
  );
}
